import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, CheckCircle2, Clock, Mail, Play, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

type DigestSettings = {
  cadence: "weekly" | "monthly";
  min_new_events: number;
  day_of_week: number; // 0 (Sun) – 6 (Sat) for weekly; 1–28 day-of-month for monthly
  hour_utc: number; // 0–23
};

const DAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const DEFAULTS: DigestSettings = {
  cadence: "weekly",
  min_new_events: 3,
  day_of_week: 1,
  hour_utc: 10,
};

type HealthState =
  | { kind: "loading" }
  | { kind: "no_subscribers" }
  | { kind: "never_fired" }
  | { kind: "ok"; lastSentAt: Date; status: "green" | "yellow" | "red"; nextExpectedAt: Date };

function formatAgo(ms: number): string {
  const hours = Math.round(ms / (60 * 60 * 1000));
  if (hours < 1) return "less than an hour";
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"}`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"}`;
}

const SeriesInterestSettingsCard = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState<DigestSettings>(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [health, setHealth] = useState<HealthState>({ kind: "loading" });

  const loadHealth = async (current: DigestSettings) => {
    const intervalMs =
      current.cadence === "monthly" ? 30 * 24 * 60 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000;
    const graceMs = 24 * 60 * 60 * 1000;

    const [{ data: tpl }, { count: subCount }] = await Promise.all([
      supabase.from("notification_templates").select("id").eq("name", "series_interest_digest").maybeSingle(),
      supabase.from("series_interest").select("id", { count: "exact", head: true }),
    ]);

    if (!subCount || subCount === 0) {
      setHealth({ kind: "no_subscribers" });
      return;
    }
    if (!tpl?.id) {
      setHealth({ kind: "never_fired" });
      return;
    }

    const { data: lastLog } = await supabase
      .from("notification_log")
      .select("sent_at")
      .eq("template_id", tpl.id)
      .eq("status", "sent")
      .order("sent_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!lastLog?.sent_at) {
      setHealth({ kind: "never_fired" });
      return;
    }

    const lastSentAt = new Date((lastLog as any).sent_at);
    const nextExpectedAt = new Date(lastSentAt.getTime() + intervalMs);
    const elapsed = Date.now() - lastSentAt.getTime();
    let status: "green" | "yellow" | "red" = "green";
    if (elapsed > intervalMs + graceMs) status = "red";
    else if (elapsed > intervalMs - graceMs) status = "yellow";
    setHealth({ kind: "ok", lastSentAt, status, nextExpectedAt });
  };

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "series_interest_digest")
        .maybeSingle();
      const next = data?.value
        ? { ...DEFAULTS, ...(data.value as Partial<DigestSettings>) }
        : DEFAULTS;
      setSettings(next);
      loadHealth(next);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("app_settings")
        .upsert({ key: "series_interest_digest", value: settings as any });
      if (error) throw error;
      toast({ title: "Saved", description: "Digest settings updated." });
    } catch (e: any) {
      toast({
        title: "Save failed",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "send-series-interest-digest",
        { body: { trigger: "manual", force: true } }
      );
      if (error) throw error;
      toast({
        title: "Digest sent",
        description: `Emails sent: ${data?.sent ?? 0}, skipped: ${data?.skipped ?? 0}.`,
      });
    } catch (e: any) {
      toast({
        title: "Send failed",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            <div>
              <CardTitle className="text-lg">Series Interest Digest</CardTitle>
              <CardDescription>
                Bundles new events into a single email for members who follow each series.
              </CardDescription>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSendNow}
            disabled={sending}
            className="gap-1.5"
          >
            <Play className="h-4 w-4" />
            {sending ? "Sending…" : "Send digest now"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Cron health indicator */}
        {(() => {
          if (health.kind === "loading") {
            return (
              <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
                <Clock className="h-4 w-4" />
                Checking digest health…
              </div>
            );
          }
          if (health.kind === "no_subscribers") {
            return (
              <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
                <Clock className="h-4 w-4" />
                No subscribers yet — nothing to send.
              </div>
            );
          }
          if (health.kind === "never_fired") {
            return (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Cron has never fired since deployment.</p>
                  <p className="mt-0.5 opacity-90">
                    Check <code>app.settings.anon_key</code> and <code>cron.job_run_details</code> in the database.
                  </p>
                </div>
              </div>
            );
          }
          const ago = formatAgo(Date.now() - health.lastSentAt.getTime());
          if (health.status === "green") {
            return (
              <div className="flex items-center gap-2 rounded-md border border-success/40 bg-success/5 p-3 text-xs text-success">
                <CheckCircle2 className="h-4 w-4" />
                Last digest sent {ago} ago.
              </div>
            );
          }
          if (health.status === "yellow") {
            return (
              <div className="flex items-center gap-2 rounded-md border border-warning/40 bg-warning/5 p-3 text-xs text-warning-foreground">
                <Clock className="h-4 w-4" />
                Last digest sent {ago} ago — next expected within 24h.
              </div>
            );
          }
          return (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">⚠️ Last digest sent {ago} ago. Expected by {health.nextExpectedAt.toLocaleString()}.</p>
                <p className="mt-0.5 opacity-90">
                  Cron may be stuck — check <code>app.settings.anon_key</code> and <code>cron.job_run_details</code>.
                </p>
              </div>
            </div>
          );
        })()}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Cadence</Label>
            <Select
              value={settings.cadence}
              onValueChange={(v) =>
                setSettings((s) => ({ ...s, cadence: v as "weekly" | "monthly" }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly (1st of month)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Minimum new events</Label>
            <Input
              type="number"
              min={1}
              max={50}
              value={settings.min_new_events}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  min_new_events: Math.max(1, Number(e.target.value) || 1),
                }))
              }
            />
            <p className="text-xs text-muted-foreground">
              Skip a digest if a user has fewer than this many new events.
            </p>
          </div>

          {settings.cadence === "weekly" && (
            <div className="space-y-2">
              <Label>Day of week</Label>
              <Select
                value={String(settings.day_of_week)}
                onValueChange={(v) =>
                  setSettings((s) => ({ ...s, day_of_week: Number(v) }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map((d) => (
                    <SelectItem key={d.value} value={String(d.value)}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Hour (UTC)</Label>
            <Input
              type="number"
              min={0}
              max={23}
              value={settings.hour_utc}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  hour_utc: Math.max(0, Math.min(23, Number(e.target.value) || 0)),
                }))
              }
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="gap-1.5">
            <Save className="h-4 w-4" />
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SeriesInterestSettingsCard;
